import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ForwardedRef, CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg';

export interface IconProps extends Omit<
  ComponentPropsWithoutRef<LucideIcon>,
  'ref' | 'size' | 'width' | 'height' | 'aria-label' | 'aria-hidden' | 'role'
> {
  /** The lucide glyph to render, e.g. `as={ChevronDown}`. */
  as: LucideIcon;
  /** Size from the --icon-<size> token scale. Default 'md' (20px). */
  size?: IconSize;
  /** className passthrough — use for color (e.g. `text-muted`) and animation (`animate-spin`). */
  className?: string;
  /**
   * Accessible label. Omit (default) for decorative icons sitting next to text — the
   * icon renders aria-hidden. Provide it for a standalone, meaningful icon — the icon
   * becomes role="img" with this label.
   */
  label?: string;
}

/**
 * The sanctioned way to render an icon (GATE: icon-discipline). Dimensions come from the
 * --icon-<size> token scale in tokens.css, applied as inline width/height so a single token
 * edit resizes every icon — no ad-hoc size={N} / h-_ w-_. Color is inherited (currentColor),
 * so set it with a text-* class. Decorative by default; pass `label` to expose it to AT.
 */
export const Icon = forwardRef(function Icon(
  { as: Glyph, size = 'md', label, className, style, ...rest }: IconProps,
  ref: ForwardedRef<SVGSVGElement>,
) {
  const dim = `var(--icon-${size})`;
  const merged: CSSProperties = { ...style, width: dim, height: dim };
  return (
    <Glyph
      ref={ref}
      {...rest}
      data-icon-size={size}
      className={className}
      style={merged}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
    />
  );
});
